import { cn } from "@/lib/utils";
import type { HTMLAttributes, ReactNode } from "react";

export type StatusVariant = "success" | "warning" | "danger" | "info" | "neutral";

const styles: Record<StatusVariant, string> = {
  success:
    "bg-[hsl(var(--success)/0.12)] text-[hsl(var(--success))] ring-1 ring-[hsl(var(--success)/0.25)]",
  warning:
    "bg-[hsl(var(--warning)/0.14)] text-[hsl(var(--warning-foreground))] ring-1 ring-[hsl(var(--warning)/0.35)]",
  danger:
    "bg-[hsl(var(--destructive)/0.12)] text-[hsl(var(--destructive))] ring-1 ring-[hsl(var(--destructive)/0.28)]",
  info:
    "bg-[hsl(var(--info)/0.12)] text-[hsl(var(--info))] ring-1 ring-[hsl(var(--info)/0.28)]",
  neutral:
    "bg-muted text-muted-foreground ring-1 ring-border",
};

interface StatusPillProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: StatusVariant;
  icon?: ReactNode;
  dot?: boolean;
}

/**
 * Canonical status pill — one of 5 semantic variants.
 * Retires ad-hoc `bg-*-500/10 text-*-600` combos scattered across pages.
 */
export function StatusPill({
  variant = "neutral",
  icon,
  dot,
  className,
  children,
  ...rest
}: StatusPillProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium leading-5",
        styles[variant],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span
          aria-hidden
          className="h-1.5 w-1.5 rounded-full bg-current opacity-80"
        />
      )}
      {icon}
      {children}
    </span>
  );
}

export default StatusPill;
